import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { FaPlay as PlayIcon, FaStop as StopIcon } from 'react-icons/fa'
import {
  selectCurrentContainer,
  selectCurrentProject,
  useSequencerStore,
} from '@/stores/sequencerStore'

/**
 * Compact transport chip that lives in the workspace header across
 * `/projects/$id`, `/midi`, and `/slicer`. Lets the user start / stop
 * the sequencer without going back to the editor.
 *
 * Known limitation (tracked for a follow-up): when the user is NOT
 * on the sequencer editor, the audio scheduler isn't mounted — the
 * store still flips `isPlaying` but no sound comes out. The chip
 * handles this by navigating to `/projects/$currentContainerId`
 * before flipping Play so playback always happens with the scheduler
 * mounted. A future wave extracts the scheduler into a root-level
 * provider so playback genuinely continues across workspace switches.
 */
export const TransportChip: React.FC<{
  /** When true, assume the user is already on the editor and the
   *  scheduler is mounted — just flip play state instead of navigating. */
  inEditor?: boolean
}> = ({ inEditor = false }) => {
  const isPlaying = useSequencerStore(s => s.isPlaying)
  const setPlaying = useSequencerStore(s => s.setPlaying)
  const currentContainerId = useSequencerStore(s => s.currentContainerId)
  const container = useSequencerStore(selectCurrentContainer)
  const project = useSequencerStore(selectCurrentProject)
  const navigate = useNavigate()

  const handleToggle = () => {
    if (!inEditor && !isPlaying) {
      // Route to the editor first so the scheduler's there to hear.
      navigate({
        to: '/werkshop/projects/$id',
        params: { id: currentContainerId },
      })
    }
    setPlaying(!isPlaying)
  }

  const label = project ? project.name : 'No pattern'
  const bankLabel = container
    ? container.banks[container.activeBankId]?.label
    : null

  return (
    <button
      type="button"
      onClick={handleToggle}
      aria-label={isPlaying ? 'Stop playback' : 'Start playback'}
      title={
        isPlaying
          ? `Stop · ${bankLabel ?? ''} · ${label}`
          : `Play · ${bankLabel ?? ''} · ${label}`
      }
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-mono transition-colors ${
        isPlaying
          ? 'bg-accent text-[color:var(--accent-ink)] border border-accent shadow-[0_0_8px_var(--accent-halo)]'
          : 'bg-surface-sunken text-content-secondary border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-content-primary'
      }`}
    >
      <span aria-hidden className="leading-none flex items-center">
        {isPlaying ? <StopIcon size={9} /> : <PlayIcon size={9} />}
      </span>
      <span className="hidden sm:inline">{isPlaying ? 'Stop' : 'Play'}</span>
      {project && (
        <span
          aria-hidden
          className={`hidden md:inline text-[10px] ${isPlaying ? 'opacity-80' : 'opacity-60'}`}
        >
          {project.bpm} BPM
        </span>
      )}
    </button>
  )
}
